import type {
  JobTeaserVisibleJobFragment,
  DreamMachineTakenJobFragment,
} from '@xing-com/crate-jobs-graphql';

import {
  isValidSalary,
  isValidSalaryEstimate,
  isValidSalaryRange,
} from './salary';

const NOT_BOOKMARKED = 'NOT_BOOKMARKED';

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export const selectAvailableJobData = (job: JobTeaserVisibleJobFragment) => {
  const {
    activeUntil,
    id,
    companyInfo,
    salary,
    refreshedAt,
    title,
    location,
    redirectsToThirdPartyUrl,
    application,
  } = job;

  const companyName = companyInfo?.companyNameOverride;
  const companyLogo = companyInfo?.company?.logos?.x1 || '';
  const employmentTypeValue = job.employmentType?.localizationValue;
  const bookmarkState = job.userInteractions?.bookmark?.state ?? NOT_BOOKMARKED;
  const isBookmarked = bookmarkState !== NOT_BOOKMARKED;
  const applyUrl =
    application?.__typename === 'UrlApplication' ? application.applyUrl : '';

  const showSalary =
    isValidSalary(salary) ||
    isValidSalaryRange(salary) ||
    isValidSalaryEstimate(salary);

  return {
    activeUntil,
    id,
    salary,
    showSalary,
    refreshedAt,
    title,
    location,
    locations: job.locations,
    companyName,
    companyLogo,
    employmentTypeValue,
    isBookmarked,
    redirectsToThirdPartyUrl,
    applyUrl,
  };
};

const selectTakenJobData = (
  jobData: DreamMachineTakenJobFragment
): ReturnType<typeof selectAvailableJobData> => {
  const job = jobData?.job;

  const showSalary =
    isValidSalary(job?.salary) ||
    isValidSalaryRange(job?.salary) ||
    isValidSalaryEstimate(job?.salary);

  return {
    activeUntil: undefined,
    id: '',
    salary: jobData?.job?.salary,
    showSalary: showSalary,
    refreshedAt: '',
    title: job?.jobTitle || '',
    location: {
      city: job?.company?.address?.city || job?.fallbackCompany?.address?.city,
    },
    locations: [
      {
        __typename: 'JobLocation',
        city:
          job?.company?.address?.city || job?.fallbackCompany?.address?.city,
      },
    ],
    companyName:
      job?.company?.companyName || job?.fallbackCompany?.companyName || '',
    companyLogo: job?.company?.logos?.logo256px || '',
    employmentTypeValue: undefined,
    isBookmarked: false,
    redirectsToThirdPartyUrl: false,
    applyUrl: '',
  };
};

export const selectJobData = (
  job: JobTeaserVisibleJobFragment | DreamMachineTakenJobFragment
): ReturnType<typeof selectAvailableJobData> => {
  if (job.__typename === 'TakenJob') {
    return selectTakenJobData(job);
  }
  if (job.__typename === 'VisibleJob') {
    return selectAvailableJobData(job);
  }

  // Return a default object with safe fallback values
  return {
    activeUntil: undefined,
    id: '',
    salary: undefined,
    showSalary: false,
    refreshedAt: '',
    title: '',
    location: {},
    locations: [],
    companyName: '',
    companyLogo: '',
    employmentTypeValue: undefined,
    isBookmarked: false,
    redirectsToThirdPartyUrl: false,
    applyUrl: '',
  };
};
